import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getUserClaimedTeams } from '../utils/teamUtils';
import {
  TeamService,
  LeagueService,
  SeasonService
} from '../services/firebaseService';
import { USER_ROLES } from '../models/dataModels';
import { UserCircleIcon } from '@heroicons/react/24/outline';

export default function ProfileSettings() {
  const { currentUser, userProfile } = useAuth();
  const [teamsData, setTeamsData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadTeams = async () => {
      if (!userProfile || !userProfile.teams) {
        setLoading(false);
        return;
      }

      try {
        const claimedTeams = getUserClaimedTeams(userProfile.teams);
        
        if (claimedTeams.length === 0) {
          setLoading(false);
          return;
        }

        // Load full data for each team
        const teamsDataPromises = claimedTeams.map(async (claimedTeam) => {
          try {
            const [team, league, season] = await Promise.all([
              TeamService.getTeam(claimedTeam.leagueId, claimedTeam.seasonId, claimedTeam.teamId),
              LeagueService.getLeague(claimedTeam.leagueId),
              SeasonService.getSeason(claimedTeam.seasonId)
            ]);

            return {
              team,
              league,
              season,
              claimedAt: claimedTeam.claimedAt
            };
          } catch (err) {
            console.error(`Error loading team ${claimedTeam.teamId}:`, err);
            return null;
          }
        });

        const loadedTeams = (await Promise.all(teamsDataPromises)).filter(Boolean);
        setTeamsData(loadedTeams);
      } catch (err) {
        console.error('Error loading teams:', err);
      } finally {
        setLoading(false);
      }
    };

    loadTeams();
  }, [userProfile]);

  const formatRole = (role) => {
    return role.replace(/([A-Z])/g, ' $1').trim();
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (error) {
      return 'N/A';
    }
  };

  if (!currentUser || !userProfile) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <p className="text-gray-600">Please sign in to view your profile.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Profile Settings</h1>
          <p className="mt-2 text-gray-600">View and manage your account information</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center mb-6">
            <UserCircleIcon className="w-16 h-16 text-gray-400 mr-4" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {userProfile.displayName || currentUser.email}
              </h2>
              <p className="text-sm text-gray-500 mt-1">Account Information</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="border-b border-gray-200 pb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <p className="text-gray-900">{currentUser.email}</p>
            </div>

            <div className="border-b border-gray-200 pb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Display Name
              </label>
              <p className="text-gray-900">{userProfile.displayName || 'Not set'}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Roles
              </label>
              <div className="flex flex-wrap gap-2">
                {userProfile.roles && userProfile.roles.length > 0 ? (
                  userProfile.roles.map((role) => (
                    <span
                      key={role}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-survivor-100 text-survivor-800"
                    >
                      {formatRole(role)}
                    </span>
                  ))
                ) : (
                  <span className="text-gray-500">No roles assigned</span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Your Teams</h2>
          
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-survivor-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading teams...</p>
            </div>
          ) : teamsData.length > 0 ? (
            <div className="space-y-4">
              {teamsData.map((teamData, index) => (
                <div
                  key={`${teamData.team.id}-${index}`}
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {teamData.team.name}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {teamData.league.name} • Season {teamData.season.id || teamData.season.name || 'N/A'}
                      </p>
                      {teamData.claimedAt && (
                        <p className="text-xs text-gray-500 mt-1">
                          Claimed: {formatDate(teamData.claimedAt)}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        Score: {teamData.team.currentScore || 0}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>You haven't claimed any teams yet.</p>
              <p className="text-sm mt-2">Claim a team to get started with predictions!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

